/*
  store/index.js
    root store object
    storeをまとめてexportする
*/

import Vue from 'vue'
import Vuex from 'vuex'
import moment from 'moment'
import { LS_TOKEN_KEY, /*formatDate,*/ formatDateMoment, isNullorUndefined } from '@/common'
import { sLog } from '@/common/simple-logger'
// @Todo 独自アイコンのためのサンプル実装
/*
import inoffice from '@/assets/inoffice.png'
import outofwork from '@/assets/outofwork.png'
import outoffice from '@/assets/outoffice.png'
*/
import Auth from '@/store/modules/auth'
import LoginUser from '@/store/modules/login-user'
import CurrentPage from '@/store/modules/current-page'
import SystemSettings from '@/store/modules/system-settings'
import GeoLocation from '@/store/modules/geolocation'
import Telework from '@/store/modules/telework'
import * as types from '@/store/mutation-types'
import {
  getUserList,
  getWhereaboutsInfoByMember,
  getWhereaboutsList,
  getSectionList,
  getGroupList,
  getClassList,
  getPlaceList,
  getPresenceStatusList,
  updateWhereaboutsInfo,
  //getCalendarKeyByMember,
} from '@/api'

Vue.use(Vuex)

export default new Vuex.Store({
  modules: {
    auth: Auth,
    loginUser: LoginUser,
    currentPage: CurrentPage,
    systemSettings: SystemSettings,
    geoLocation: GeoLocation,
    telework: Telework,
  },
  state: {
    sIsMobile: false,  // Mobile端末を使っているか
    sIsDeviceReady: false,  // Status of Device
    sUserList: [],  // ユーザ基本情報
    sWhereaboutsList: [],  // 行動予定情報リスト
    sSelectedMemberId: '',  // 選択されたユーザID
    sSelectedWhereaboutsInfo: {},  // 選択されたユーザの行先情報
    sWhSelectedOnMenu: false,  // メニューからの行先情報選択
    sSectionList: [],  // セクション一覧
    sGroupList: [],  // グループ一覧
    sSelectedGroupId: '',  // 選択されているグループID
    sClassList: [],  // 役職一覧
    sPlaceList: [],  // 行先一覧
    sPresenceStatusList: [],  // 在席状態一覧
    sUpbtnVisible: false,  // 送信ボタンの表示/非表示
    sIsEdit: false,  // Memoなどの入力モードか
    sSendGnssVisible: false,  // GNSS送信ボタンの表示/非表示
    sIsWhereaboutsPage: true,  // 行動予定一覧画面表示中かどうか
  },
  getters: {
    isMobile: state => state.sIsMobile,
    isDeviceReady: state => state.sIsDeviceReady,
    isUpbtnVisible: state => state.sUpbtnVisible,
    selectedMemberId: state => state.sSelectedMemberId,
    selectedWhereaboutsInfo: state => state.sSelectedWhereaboutsInfo,
    selectedGroupId: state => state.sSelectedGroupId,
    userList: state => state.sUserList,
    fSection: (state) => (secId) => {
      return state.sSectionList.filter((elm) => {
        return elm.section_id.toString() === secId.toString()
      })
    },
    fGroup: (state) => (gId) => {
      return state.sGroupList.filter((elm) => {
        return elm.group_id.toString() === gId.toString()
      })
    },
    fClass: (state) => (cId) => {
      return state.sClassList.filter((elm) => {
        return elm.class_id.toString() === cId.toString()
      })
    },
    fClassByName: (state) => (cName) => {
      // 部分一致検索
      return state.sClassList.filter((elm) => {
        return elm.c_name.indexOf(cName) !== -1
      })
    },
    fStateByName: (state) => (stateName) => {
      // 部分一致検索
      return state.sPresenceStatusList.filter((elm) => {
        return elm.st_name.indexOf(stateName) !== -1
      })
    },
    isEdit: state => state.sIsEdit,
    isWhereaboutsPage: state => state.sIsWhereaboutsPage,
  },
  actions: {
    // -------------------------------------------------------------------------
    // ユーザ情報取得アクション
    async aGetUserList (context, companyId) {
      sLog.debug('@action aGetUserList ' + companyId)

      let result = await getUserList(companyId, localStorage.getItem(LS_TOKEN_KEY))
      if (!result.error && !result.body.error) {
        // 取得したらstoreに保存
        context.commit(types.SET_USER_LIST, result.body.body)
      }

      return result
    },
    // -------------------------------------------------------------------------
    // 行先情報リスト取得アクション
    async aGetWhereaboutsList (context, payload) {
      sLog.debug('@action aGetWhereaboutsList ' + JSON.stringify(payload))

      if (isNullorUndefined(payload.companyId)) {
        sLog.error('payload is null or undefined.')
        return {
          error: true,
          status: 'argument is null',
        }
      }

      const result = await getWhereaboutsList(
        payload.companyId,
        payload.sectionId,
        payload.groupId,
        localStorage.getItem(LS_TOKEN_KEY)
      )
      if (!result.error && !result.body.error) {
        // 取得したらstoreに保存
        context.commit(types.SET_WHEREABOUTS_LIST, result.body.body)
      }

      return result
    },
    // -------------------------------------------------------------------------
    // 行先情報リスト取得アクション
    async aGetWhereaboutsInfoByMember (context, payload) {
      sLog.debug('@action aGetWhereaboutsInfoByMember ' + JSON.stringify(payload))

      if (isNullorUndefined(payload.companyId)) {
        sLog.error('payload is null or undefined.')
        return {
          error: true,
          status: 'argument is null',
        }
      }

      const result = await getWhereaboutsInfoByMember(
        payload.companyId,
        payload.userId,
        localStorage.getItem(LS_TOKEN_KEY)
      )
      if (!result.error && !result.body.error) {
        context.commit(types.SELECT_USER, result.body.body)
      }
      else {
        sLog.error('failed get whereabouts info on aSelectUser')
      }

      return result
    },
    // -------------------------------------------------------------------------
    // セクション取得アクション
    async aGetSectionList (context, companyId) {
      sLog.debug('@action aGetSectionList')

      let result = await getSectionList(companyId, localStorage.getItem(LS_TOKEN_KEY))
      if (!result.error && !result.body.error) {
        // 取得したらstoreに保存
        context.commit(types.SET_SECTION_LIST, result.body.body)
      }

      return result
    },
    // -------------------------------------------------------------------------
    // グループリスト取得アクション
    async aGetGroupList (context, companyId) {
      sLog.debug('@action aGetGroupList')

      let result = await getGroupList(companyId, localStorage.getItem(LS_TOKEN_KEY))
      if (!result.error && !result.body.error) {
        // 取得したらstoreに保存
        context.commit(types.SET_GROUP_LIST, result.body.body)
      }

      return result
    },
    // -------------------------------------------------------------------------
    // 役職取得アクション
    async aGetClassList (context, companyId) {
      sLog.debug('@action aGetClassList')

      let result = await getClassList(companyId, localStorage.getItem(LS_TOKEN_KEY))
      if (!result.error && !result.body.error) {
        // 取得したらstoreに保存
        context.commit(types.SET_CLASS_LIST, result.body.body)
      }

      return result
    },
    // -------------------------------------------------------------------------
    // 行先リスト取得アクション
    async aGetPlaceList (context, companyId) {
      sLog.debug('@action aGetPlaceList')
      const result = await getPlaceList(companyId, localStorage.getItem(LS_TOKEN_KEY))
      if (!result.error && !result.body.error) {
        // 取得したらstoreに保存
        context.commit(types.SET_PLACE_LIST, result.body.body)
      }
      return result
    },
    // -------------------------------------------------------------------------
    // 在席状態リスト取得アクション
    async aGetPresenceStatusList (context, companyId) {
      sLog.debug('@action aGetPresenceStatusList')
      const result = await getPresenceStatusList(companyId, localStorage.getItem(LS_TOKEN_KEY))
      if (!result.error && !result.body.error) {
        // 取得したらstoreに保存
        context.commit(types.SET_PRESENCE_STATUS_LIST, result.body.body)
      }
      return result
    },
    // -------------------------------------------------------------------------
    // ユーザ選択時のアクション
    //   行先情報とカレンダー情報を取得する
    //
    // @remark nullの場合は非選択
    aSelectUser: async (context, payload) => {
      const state = context.state

      if (isNullorUndefined(payload)) {
        sLog.debug('@action aSelectUser User Unselected(' + state + ')')
        state.sSelectedMemberId = null
        context.commit(types.SELECT_USER, null)
        return
      }

      sLog.debug('@action aSelectUser ' + (payload && payload.memberId))
      state.sSelectedMemberId = payload.memberId

      // カレンダー情報取得
      /*
      const result = await getCalendarKeyByMember(payload.companyId, payload.memberId)
      if (isNullorUndefined(result.skey)) {
        sLog.warn('calendar key is null : ' + payload.memberId)
      }
      context.commit(types.SET_CALENDAR_KEY, result.skey)
      */

      // 選択されたユーザの情報がリストに含まれるか検索
      let whereaboutsInfo = state.sWhereaboutsList.find(function (elm) {
        return (elm.user_id === state.sSelectedMemberId)
      })
      // 行先情報を更新
      let whereabouts_info = null
      if (!isNullorUndefined(whereaboutsInfo)) {
        // リストから行先情報をDeepCopyする
        // (Object.assignでは完全なDeepCopyにならないことに注意.組み込み型以外をメンバ変数に持つ場合は使用不可)
        whereabouts_info = Object.assign({}, whereaboutsInfo)
      }
      else {
        // 選択されたユーザの情報が見つからない場合は、サーバからユーザ情報を取得する
        sLog.debug('user id : ' + payload.memberId + ' not found in list.')
        const result = await getWhereaboutsInfoByMember(
          payload.companyId,
          payload.memberId,
          localStorage.getItem(LS_TOKEN_KEY)
        )
        if (!result.error && !result.body.error) {
          whereabouts_info = result.body.body
        }
        else {
          sLog.error('failed get whereabouts info on aSelectUser')
        }
      }
      sLog.verbose(JSON.stringify(whereabouts_info))
      context.commit(types.SELECT_USER, whereabouts_info)
    },
    // -------------------------------------------------------------------------
    // 選択ユーザの行先情報取得アクション
    aGetSelectUser: async (context, payload) => {
      const state = context.state

      if (isNullorUndefined(payload)) {
        sLog.debug('@action aGetSelectUser User Unselected(' + state + ')')
        state.sSelectedMemberId = null
        context.commit(types.SELECT_USER, null)
        return
      }
      state.sSelectedMemberId = payload.memberId

      // 一旦行先リストから選択されたユーザの情報を取得する
      // (行先一覧→詳細に切り替えた際に、データがない状態の詳細画面が一瞬表示されるため)
      /*let whereaboutsInfo = state.sWhereaboutsList.find(function (elm) {
        return (elm.user_id === state.sSelectedMemberId)
      })
      if (!isNullorUndefined(whereaboutsInfo)) {
        // リストから行先情報をDeepCopyする
        // (Object.assignでは完全なDeepCopyにならないことに注意.組み込み型以外をメンバ変数に持つ場合は使用不可)
        let whereabouts_info = Object.assign({}, whereaboutsInfo)
        context.commit(
          types.SELECT_USER,
          {
            company_id: whereabouts_info.company_id,
            user_id: whereabouts_info.user_id,
            staff_id: whereabouts_info.staff_id,
            name: whereabouts_info.name,
            class: whereabouts_info.class,
            update_date: whereabouts_info.update_date
          })
      }*/

      // サーバからユーザ情報の最新情報を取得する
      sLog.debug('user id : ' + payload.memberId)
      const result = await getWhereaboutsInfoByMember(
        payload.companyId,
        payload.memberId,
        localStorage.getItem(LS_TOKEN_KEY)
      )
      if (!result.error && !result.body.error) {
        sLog.debug('get whereabouts info[' + JSON.stringify(result.body.body) + ']')
        context.commit(types.SELECT_USER, result.body.body)
      }
      else {
        sLog.error('failed get whereabouts info on aGetSelectUser')
      }
    },
    // -------------------------------------------------------------------------
    // 場所情報設定アクション
    aSetWhereaboutsInfo: (context, payload) => {
      sLog.debug('@action aSetWhereaboutsInfo ' + JSON.stringify(payload))
      context.commit(types.SET_WHEREABOUTS_INFO, payload)
    },
    // -------------------------------------------------------------------------
    // 行動予定情報更新アクション
    async aUpdateWhereaboutsInfo (context, payload) {
      sLog.debug('@action aUpdateWhereaboutsInfo')
      const result = await updateWhereaboutsInfo(
        payload.companyId,
        payload.loginUserId,
        context.state.sSelectedWhereaboutsInfo,
        localStorage.getItem(LS_TOKEN_KEY)
      )
      if (!result.error && !result.body.error) {
        // 成功したらstoreに反映する
        context.commit(types.SET_WHEREABOUTS_INFO_TO_LIST, context.state.sSelectedWhereaboutsInfo, result.body.update_date)
        context.commit(types.SET_SELECT_USER_UPDATE_DATE, result.body.update_date)
      }
      else {
        sLog.warn('failed update whereabouts info')
      }
      return result
    },
    // -------------------------------------------------------------------------
    aChangeWASelectedOnMenu ({ commit }, payload) {
      commit(types.SET_WA_SELECTED_ON_MENU, payload)
    },
  },
  mutations: {
    // -------------------------------------------------------------------------
    // set user list
    [types.SET_USER_LIST] (state, userList) {
      state.sUserList = userList
      sLog.verbose(JSON.stringify(state.sUserList))
    },
    // -------------------------------------------------------------------------
    // set section list
    [types.SET_SECTION_LIST] (state, sectionList) {
      state.sSectionList = sectionList
    },
    // -------------------------------------------------------------------------
    // set group list
    [types.SET_GROUP_LIST] (state, groupList) {
      state.sGroupList = groupList
    },
    // -------------------------------------------------------------------------
    // set class list
    [types.SET_CLASS_LIST] (state, classList) {
      state.sClassList = classList
    },
    // -------------------------------------------------------------------------
    // set place list
    [types.SET_PLACE_LIST] (state, placeList) {
      let convList = []
      let convData = {}
      for (let i in placeList) {
        convData = {
          dist_id: placeList[i].dist_id.toString(),
          dist_name: placeList[i].dist_name
        }
        convList.push(convData)
      }
      state.sPlaceList = convList
    },
    // -------------------------------------------------------------------------
    // set presence status list
    [types.SET_PRESENCE_STATUS_LIST] (state, presenceStatusList) {
      state.sPresenceStatusList = presenceStatusList
    },
    // -------------------------------------------------------------------------
    // selected user information
    [types.SELECT_USER] (state, whereaboutsInfo) {
      // 行先情報を更新
      if (!isNullorUndefined(whereaboutsInfo)) {
        state.sSelectedWhereaboutsInfo = _convertWhereaboutsInfo(whereaboutsInfo, state.sPlaceList)
      }
      else {
        state.sSelectedWhereaboutsInfo = {}
      }
    },
    // -------------------------------------------------------------------------
    // set Whereabouts List
    //   storeの行動予定情報リストをセットする
    //setWhereaboutsList: function (state, whereaboutsList) {
    [types.SET_WHEREABOUTS_LIST] (state, whereaboutsList) {
      sLog.debug(whereaboutsList)
      state.sWhereaboutsList = _convertWhereaboutsList(whereaboutsList, state.sPlaceList)
      /* 以下を実装していた理由が不明
      // Listが更新されたらSelectMember情報も更新する
      let whereaboutsInfo = state.sWhereaboutsList.find(function (elm) {
        return (elm.user_id === state.sSelectedMemberId)
      })
      if (whereaboutsInfo !== undefined) {
        state.sSelectedWhereaboutsInfo = whereaboutsInfo
        sLog.debug(JSON.stringify(state.sSelectedWhereaboutsInfo))
      }
      else {
        state.sSelectedWhereaboutsInfo = {}
      }
      */
    },
    // -------------------------------------------------------------------------
    // set whereabouts information
    [types.SET_WHEREABOUTS_INFO] (state, whereaboutsInfo) {
      state.sSelectedWhereaboutsInfo.dist = whereaboutsInfo.dist
      state.sSelectedWhereaboutsInfo.dist_name = whereaboutsInfo.dist_name
      state.sSelectedWhereaboutsInfo.comment = whereaboutsInfo.comment
      state.sSelectedWhereaboutsInfo.ret_date = whereaboutsInfo.ret_date
      state.sSelectedWhereaboutsInfo.ret_time = whereaboutsInfo.ret_time
    },
    // -------------------------------------------------------------------------
    // set whereabouts information to list
    [types.SET_WHEREABOUTS_INFO_TO_LIST] (state, whereaboutsInfo, update_date) {
      let whereabouts = state.sWhereaboutsList.find(function (elm) {
        return (elm.user_id === whereaboutsInfo.user_id)
      })
      console.assert('user not found.', !isNullorUndefined(whereabouts))
      if (!isNullorUndefined(whereabouts)) {
        whereaboutsInfo = _convertWhereaboutsInfo(whereaboutsInfo, state.sPlaceList)
        whereabouts.dist = whereaboutsInfo.dist
        whereabouts.dist_name = whereaboutsInfo.dist_name
        whereabouts.comment = whereaboutsInfo.comment
        whereabouts.state = whereaboutsInfo.state
        whereabouts.ret_date = whereaboutsInfo.ret_date
        whereabouts.v_ret_date = whereaboutsInfo.v_ret_date
        whereabouts.ret_time = whereaboutsInfo.ret_time
        whereabouts.v_ret_time = whereaboutsInfo.v_ret_time
        whereabouts.move_direct = whereaboutsInfo.move_direct
        whereabouts.update_date = update_date
      }
    },
    // -------------------------------------------------------------------------
    // set update_date to selected user info
    [types.SET_SELECT_USER_UPDATE_DATE] (state, update_date) {
      state.sSelectedWhereaboutsInfo.update_date = update_date
    },
    // -------------------------------------------------------------------------
    // set calendar key
    /*[types.SET_CALENDAR_KEY] (state, calendarKey) {
      state.sSelectedMemberCalendarKey = calendarKey
    },*/
    // -------------------------------------------------------------------------
    [types.SET_WA_SELECTED_ON_MENU] (state, isSelected) {
      state.sWhSelectedOnMenu = isSelected
    },
    // -------------------------------------------------------------------------
    // set Is Movile
    setIsMobile: function (state, status) {
      state.sIsMobile = status
    },
    // -------------------------------------------------------------------------
    // set device ready
    setIsDeviceReady: function (state, status) {
      state.sIsDeviceReady = status
    },
    // -------------------------------------------------------------------------
    //  set server communication status
    setSvComStatus: function (state, status) {
      state.sSvSnackbar = status
    },
    // -------------------------------------------------------------------------
    setUpbtnVisible: function (state, visible) {
      state.sUpbtnVisible = visible
    },
    // -------------------------------------------------------------------------
    // set Is Edit mode
    setIsEdit: function (state, status) {
      state.sIsEdit = status
    },
    // -------------------------------------------------------------------------
    setSendGnssVisible: function (state, visible) {
      state.sSendGnssVisible = visible
    },
    // -------------------------------------------------------------------------
    // set Is Whereabouts page
    setIsWhereaboutsPage: function (state, status) {
      state.sIsWhereaboutsPage = status
    },
  },
})

function _convertWhereaboutsList (whereaboutsList, placeList) {
  let whereabouts_list = []
  if (whereaboutsList.length <= 0) {
    return whereabouts_list
  }
  for (let i in whereaboutsList) {
    whereabouts_list.push(_convertWhereaboutsInfo(whereaboutsList[i], placeList))
  }

  return whereabouts_list
}

function _convertWhereaboutsInfo (whereaboutsInfo, placeList) {
  let whereabouts = {}
  whereabouts.staff_id = whereaboutsInfo.staff_id
  whereabouts.user_id = whereaboutsInfo.user_id
  whereabouts.name = whereaboutsInfo.name
  whereabouts.name_phonetic = isNullorUndefined(whereaboutsInfo.name_phonetic) ? '' : whereaboutsInfo.name_phonetic
  whereabouts.class = whereaboutsInfo.class
  whereabouts.state = whereaboutsInfo.state
  whereabouts.dist = whereaboutsInfo.dist || ''
  whereabouts.dist_name = placeIdToStr(whereabouts.dist, placeList)
  whereabouts.comment = whereaboutsInfo.comment || ''
  whereabouts.return = whereaboutsInfo.return
  // 表示用の日付(Component側で実装すると、うまくBindされず表示が更新されないのでデータとして保持する)
  whereabouts.ret_date = null
  whereabouts.v_ret_date = ''
  //let date = whereaboutsInfo.ret_date || '-'
  let date = whereaboutsInfo.ret_date || ''
  //if (date !== '-') {
  if (date !== '') {
    //date = formatDate(new Date(date), 'YYYY-MM-DD')
    date = formatDateMoment(date, 'YYYY-MM-DD')
    //whereabouts.v_ret_date = formatDate(new Date(date), 'MM/DD')
    whereabouts.v_ret_date = formatDateMoment(date, 'MM/DD')
    whereabouts.ret_date = date
  }
  //whereabouts.ret_date = date
  // 表示用の時刻(Component側で実装すると、うまくBindされず表示が更新されないのでデータとして保持する)
  whereabouts.ret_time = null
  whereabouts.v_ret_time = ''
  //let time = whereaboutsInfo.ret_time || '-'
  let time = whereaboutsInfo.ret_time || ''
  //if (time !== '-') {
  if (time !== '') {
    //let s_date = formatDate(new Date(), 'YYYY-MM-DD')  // 今日のDateを取得
    //s_date = s_date + 'T' + time  // ISO形式の文字列に変換
    //time = formatDate(new Date(s_date), 'hh:mm')
    let s_date = formatDateMoment(moment(), 'YYYY-MM-DD')  // ダミー日付として今日のDateを取得
    s_date = s_date + 'T' + time  // ISO形式の文字列に変換
    time = formatDateMoment(s_date, 'HH:mm')
    whereabouts.v_ret_time = time
    whereabouts.ret_time = time
  }
  //whereabouts.ret_time = time
  whereabouts.move_direct = Number(whereaboutsInfo.move_direct)
  whereabouts.telework_flg = whereaboutsInfo.telework_flg
  whereabouts.attendance_flg = whereaboutsInfo.attendance_flg
  whereabouts.latest_wh_category = whereaboutsInfo.latest_wh_category
  whereabouts.teleworking_over_flg = whereaboutsInfo.teleworking_over_flg
  whereabouts.update_date = whereaboutsInfo.update_date

  return whereabouts
}

/*
  place id to string
*/
export function placeIdToStr (id, placeList) {
  if (id === '0') {
    // ID=0の場合は、行先一覧で「-----」ではなく空白を表示する
    return ''
  }
  let place_info = placeList.find((place_obj) => {
    return (id === place_obj.dist_id)
  })
  // 見つからなかった場合は、空白を返す
  return (!place_info) ? '' : place_info.dist_name
}

/*
  place string to id
*/
export function placeStrToId (str, placeList) {
  let place_info = placeList.find((place_obj) => {
    return (str === place_obj.dist_name)
  })
  // 見つからなかった場合は、空白を返す
  return (!place_info) ? '' : place_info.dist_id
}



// WEBPACK FOOTER //
// ./src/store/index.js